import {
  type CertificadoAcessosArbitral,
  mapCertificadoAcessos,
} from '@/features/arbitral-detalhes';
import {
  CODIGOS_SOLICITACAO_AR,
  CotacaoDefasadaError,
} from '@/features/secretaria-arbitral/utils/solicitacaoArErros';
import { certificadoAcessosDtoSchema } from '@/shared/validation/certificadoAcessosDtoSchemas';
import { chaveDeDataDeCalendario } from '@/shared/formatters/date';
import { buscarBytesRemotos, salvarBlobComoArquivo } from '@/shared/utils/browserActions';
import { documentoLookupService } from '@/shared/services/documentoLookupService';
import { normalizeCpfCnpj } from '@/shared/utils/documentValidation';
import { numeroDoEventoOuNulo } from '@/shared/utils/numeroDoEvento';
import { toMovimentarPendenciasBody } from '@/shared/utils/pendenciasMovimentacao';
import { ErroHttp, lerRetryAfterSegundos } from '@/shared/utils/erroHttp';
import { assertUrlNavegavelSegura, ehUrlNavegavelSegura } from '@/shared/utils/urlSegura';
import { apiGet, apiPatch, apiPost, apiPut, request } from '@/shared/utils/requestBuilder';

import { feriadosService } from '@/features/feriados';
import {
  anexarArquivosAndamento,
  aplicarCamposAndamento,
} from '@/features/secretaria-arbitral/utils/andamentoMultipart';
import {
  ConflitoIdempotenciaError,
  extrairIdDoConflito,
} from '@/features/secretaria-arbitral/utils/conflitoIdempotencia';
import { dataRecebimentoParaEnviar } from '@/features/secretaria-arbitral/utils/manifestacoes';
import { mapearPrazoApi } from '@/features/secretaria-arbitral/utils/prazosPainelMapper';
import { hojeEmSaoPaulo } from '@/features/secretaria-arbitral/utils/prazoAndamento';
import { paginaDeAlertasAtivos } from '@/features/secretaria-arbitral/utils/paginaDeAlertasAtivos';
import { normalizarSolicitacoesResposta } from '@/features/secretaria-arbitral/utils/solicitacoesResposta';
import {
  toTriagemDecisaoApiBody,
  toTriagemDecisaoLoteApiBody,
} from '@/features/secretaria-arbitral/utils/triagemPayload';
import { extrairUploadIdCriado } from '@/features/secretaria-arbitral/utils/vinculosEscrita';

import type {
  ConcluirSolicitacaoInput,
  ConcluirSolicitacaoResultado,
  DecidirSolicitacaoInput,
  DecidirSolicitacaoResultado,
  FiltroSolicitacoes,
  SolicitacoesSecretariaResposta,
} from '@/features/secretaria-arbitral/types/solicitacoes';

import type { CotacaoEnvioAr } from '@/types/arbitral/envioArTypes';

import type {
  AdicionarAdvogadoParteInput,
  AdvogadoDaParteItem,
  AlterarAdvogadoResultado,
  AlterarSituacaoParteResultado,
  AndamentoArbitralItem,
  AnexoUploadEnviado,
  DiasNaoUteisDoAno,
  ModeloAndamentoOpcao,
  PublicarAndamentoInput,
  PublicarAndamentoResultado,
  TextoModeloMaterializado,
  ReenviarLinkAcessoInput,
  ReenviarLinkAcessoResultado,
  VerificacaoFacialParteInput,
  VerificacaoFacialParteResultado,
  ArbitralAlertaDispensaMotivo,
  ArbitralAlertaItem,
  ArbitralAlertasAtivosPagina,
  ListarAlertasAtivosParams,
  ArDetalhe,
  ConsultaDocumentoParteResultado,
  CriarParteArbitralInput,
  CriarParteArbitralResultado,
  EditarAdvogadoParteInput,
  EditarParteArbitralInput,
  EditarParteArbitralResultado,
  RemoverAdvogadoResultado,
  ArAEnviarFiltros,
  ArAEnviarResult,
  ArListFiltros,
  ArListItem,
  ArListResult,
  ConsultaCepResultado,
  ReemitirCobrancaArResultado,
  RequeridoAEnviarDetalhe,
  SituacaoCobrancaAr,
  SolicitarEnvioArPayload,
  SolicitarEnvioArResultado,
  CertificadoArbitralItem,
  ChatListPage,
  ConclusoInput,
  ConclusoResult,
  CriarManifestacaoInput,
  DocumentoArbitralItem,
  ListProcessosFilters,
  LoteExecucaoResponse,
  LoteRequestInput,
  LoteValidacaoInput,
  LoteValidacaoResponse,
  ListProcessosResponse,
  ManifestacaoListItem,
  PainelGeralCounts,
  ParteCompletaItem,
  ContagemPendencias,
  CriarPendenciaPayload,
  CriarPendenciaResultado,
  ListarPendenciasFilters,
  ListarPendenciasSecretariaResult,
  MovimentarPendenciasPayload,
  MovimentarPendenciasResultado,
  PrazoPendenciaPreview,
  PrazoPainelApiItem,
  PrazosPainelContadores,
  PrazosPainelData,
  PrazosPainelQuery,
  ResumoTriagem,
  PrazosPainelResumo,
  AuditoriaTriagemItem,
  AuditoriaTriagemPage,
  ChecklistTriagemAvaliacaoInput,
  ChecklistTriagem,
  OrigemAvaliacaoChecklist,
  ProcessoArbitralListItem,
  ProcessoArbitralStatus,
  ResultadoLoteTriagem,
  TriagemDecisaoAgendada,
  TriagemDecisaoAtiva,
  TriagemDecisaoLote,
  AguardandoProcesso,
  ProcessoDetalhe,
  ProcessoDetalheAcao,
  ProcessosFacets,
  PublicarDecursoBody,
  PublicarDecursoResult,
  PublicarTransitoBody,
  PublicarTransitoResult,
  TransitoElegibilidade,
  RankingArbitroItem,
  RawProcessosListResponse,
  LiberarSentencaBody,
  LiberarSentencaResult,
  ReabrirProcessoBody,
  ReabrirProcessoResult,
  SorteioBody,
  SorteioResult,
  SorteioSugestao,
  TriagemDecisao,
  ValidacaoPendenciaAcao,
  ValidarPendenciaResult,
} from '@/features/secretaria-arbitral/types';

const LIMITE_PAGINA_AUDITORIA_TRIAGEM = 50;

const LIMITE_DE_ALERTAS_DA_TELA = 100;

/**
 * Lança `ErroHttp` com a mensagem, o `code` e o `Retry-After` do corpo de erro do core. Exportado
 * para as sub-stores da feature (ex.: `registroCitacao`), que distinguem recusas pelo código.
 */
export async function handleError(response: Response, fallback: string): Promise<never> {
  let message = fallback;
  let codigo: string | null = null;
  try {
    const body = await response.json();
    message = body?.message ?? body?.error?.message ?? fallback;
    codigo = body?.error?.code ?? body?.code ?? null;
  } catch {
    message = `${fallback} (status ${response.status})`;
  }
  throw new ErroHttp(message, response.status, lerRetryAfterSegundos(response.headers), codigo);
}

// TODO: migrar este arquivo inteiro para `parseApiResponse` de
// `shared/utils/apiResponse.ts` — o unwrap manual é a convenção local pré-existente
// nos ~30 métodos deste serviço, fora do escopo deste PR.
export function unwrapData<T>(body: unknown): T {
  if (typeof body === 'object' && body !== null && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

async function lancarConflitoIdempotencia(
  response: Response,
  opcoes: { chave: string; mensagemPadrao: string },
): Promise<never> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const mensagem =
    (typeof body === 'object' && body !== null
      ? ((body as { error?: { message?: string } }).error?.message ?? null)
      : null) ?? opcoes.mensagemPadrao;
  throw new ConflitoIdempotenciaError(mensagem, extrairIdDoConflito(body, opcoes.chave));
}

export function buildListParams(
  filters: ListProcessosFilters,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = { ...(filters.avancados ?? {}) };
  if (filters.search) params.search = filters.search;
  if (filters.tab) params.tab = filters.tab;
  if (filters.status && filters.status.length > 0) params.status = filters.status.join(',');
  if (filters.rito && filters.rito.length > 0) params.rito = filters.rito.join(',');
  if (filters.situacaoTriagem && filters.situacaoTriagem.length > 0) {
    params.situacaoTriagem = filters.situacaoTriagem.join(',');
  }
  if (filters.requerente) params.requerente = filters.requerente;
  if (filters.processoId) params.processoId = filters.processoId;
  // Lista vazia sai como `origem=` de propósito: o core responde 400, e omitir a chave faria o
  // recorte sumir em silêncio (a listagem voltaria com todas as origens e status 200).
  if (filters.origem) params.origem = filters.origem.join(',');
  if (filters.comAlerta) params.comAlerta = 'true';
  if (filters.cursor) params.cursor = filters.cursor;
  if (filters.page) params.page = filters.page;
  if (filters.limit) params.limit = filters.limit;
  if (filters.sortBy) params.sortBy = filters.sortBy;
  if (filters.sortOrder) params.sortOrder = filters.sortOrder;
  return params;
}

const TIMEOUT_LEITURA_MS = 10_000;
const TIMEOUT_PUBLICACAO_BASE_MS = 30_000;
const TIMEOUT_PUBLICACAO_POR_MB_MS = 5_000;

export function timeoutDaPublicacaoMs(arquivos: File[]): number {
  const bytes = arquivos.reduce((total, arquivo) => total + arquivo.size, 0);
  const megabytes = Math.ceil(bytes / (1024 * 1024));
  return TIMEOUT_PUBLICACAO_BASE_MS + megabytes * TIMEOUT_PUBLICACAO_POR_MB_MS;
}

export function comoChaveDeData(valor: unknown): string | null {
  return chaveDeDataDeCalendario(valor);
}

const AUTOR_DESCONHECIDO = { usuarioId: null, nome: 'Autor não identificado' };

function textoOuVazio(valor: unknown): string {
  return typeof valor === 'string' ? valor : '';
}

function textoOuNulo(valor: unknown): string | null {
  return typeof valor === 'string' && valor !== '' ? valor : null;
}

function temMinimoDeAndamento(item: unknown): item is Record<string, unknown> {
  if (typeof item !== 'object' || item === null) return false;
  const campos = item as Record<string, unknown>;
  return (
    typeof campos.id === 'string' &&
    campos.id !== '' &&
    typeof campos.tipo === 'string' &&
    typeof campos.dataCriacao === 'string' &&
    campos.dataCriacao !== ''
  );
}

function normalizarAutor(valor: unknown): AndamentoArbitralItem['autor'] {
  if (typeof valor !== 'object' || valor === null) return AUTOR_DESCONHECIDO;
  const autor = valor as Record<string, unknown>;
  const nome = textoOuVazio(autor.nome);
  return {
    usuarioId: typeof autor.usuarioId === 'string' ? autor.usuarioId : null,
    nome: nome === '' ? AUTOR_DESCONHECIDO.nome : nome,
  };
}

export function parseAndamentosProcesso(itens: unknown): AndamentoArbitralItem[] {
  if (!Array.isArray(itens)) return [];
  return itens.filter(temMinimoDeAndamento).map((bruto) => {
    return {
      ...(bruto as unknown as AndamentoArbitralItem),
      numero: numeroDoEventoOuNulo(bruto.numero),
      categoriaModelo: textoOuNulo(bruto.categoriaModelo),
      descricao: textoOuVazio(bruto.descricao),
      responsavelTipo: typeof bruto.responsavelTipo === 'string' ? bruto.responsavelTipo : null,
      isContestacao: bruto.isContestacao === true,
      conclusoAbertoId: textoOuNulo(bruto.conclusoAbertoId),
      autor: normalizarAutor(bruto.autor),
      prazoFinal: comoChaveDeData(bruto.prazoFinal),
      anexos: (Array.isArray(bruto.anexos) ? bruto.anexos : []).map((anexo) => ({
        ...anexo,
        downloadUrl: ehUrlNavegavelSegura(anexo?.downloadUrl) ? anexo.downloadUrl : null,
      })),
    };
  });
}

const CONTAGEM_ZERADA: ContagemPendencias = { ativas: 0, finalizadas: 0, todas: 0 };

export function parseListaPendencias(body: unknown): ListarPendenciasSecretariaResult {
  const payload = unwrapData<Partial<ListarPendenciasSecretariaResult>>(body) ?? {};
  const items = payload.items ?? [];
  return {
    items,
    total: payload.total ?? items.length,
    contagem: payload.contagem ?? CONTAGEM_ZERADA,
  };
}

/**
 * Adapter explícito em vez de mandar o objeto do formulário: o schema do backend recusa
 * `prazoDiasUteis` e `dataVencimento` juntos (XOR), então campo
 * vazio tem de sumir do corpo — não ir como `undefined` dentro de um spread.
 */
export function toCriarPendenciaBody(payload: CriarPendenciaPayload): Record<string, unknown> {
  return {
    clientRequestId: payload.clientRequestId,
    ...(payload.origem ? { origem: payload.origem } : {}),
    tipo: payload.tipo,
    destinatarios: payload.destinatarios,
    anexoUploadIds: payload.anexoUploadIds,
    ...(payload.texto ? { texto: payload.texto } : {}),
    ...(payload.prazoDiasUteis ? { prazoDiasUteis: payload.prazoDiasUteis } : {}),
    ...(payload.dataVencimento ? { dataVencimento: payload.dataVencimento } : {}),
  };
}

const AGUARDANDO_CONHECIDOS: readonly AguardandoProcesso[] = [
  'TRIAGEM',
  'PAGAMENTO',
  'AR',
  'CITACAO',
  'MANIFESTACAO',
  'NOMEACAO',
  'SENTENCA',
  'TRANSITO',
  'NADA',
];

function normalizarAcaoDetalhe(raw: unknown): ProcessoDetalheAcao | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const item = raw as Record<string, unknown>;
  if (typeof item.id !== 'string' || item.id.length === 0) return null;
  return {
    id: item.id,
    grupo: item.grupo === 'EXPORTACAO' ? 'EXPORTACAO' : 'EXECUCAO',
    disponivel: item.disponivel === true,
    motivo: typeof item.motivo === 'string' ? item.motivo : null,
    destaque: item.destaque === true,
  };
}

function normalizarDetalheProcesso(raw: unknown): ProcessoDetalhe {
  const bruto = (raw ?? {}) as Record<string, unknown>;
  const aguardando = bruto.aguardando as AguardandoProcesso;
  return {
    ...(bruto as unknown as ProcessoDetalhe),
    acoes: Array.isArray(bruto.acoes)
      ? bruto.acoes
          .map(normalizarAcaoDetalhe)
          .filter((acao): acao is ProcessoDetalheAcao => acao !== null)
      : [],
    aguardando: AGUARDANDO_CONHECIDOS.includes(aguardando) ? aguardando : 'NADA',
  };
}

/**
 * `undefined` quando a CHAVE não veio — e só nesse caso.
 *
 * É a feature-detection do cursor (core#1668): o validador do core não é `.strict()`,
 * então um core sem o param devolve 200 com a primeira página e descarta o `cursor` em
 * silêncio. A chave ausente é o único sinal disso, e colapsá-la em `null` faria a tela
 * ler "acabou a fila" onde o servidor só não entendeu o pedido.
 *
 * Chave presente com valor que não é string (`null`, ou lixo) → `null` = fim da cadeia:
 * o contrato suporta o param, não há continuação.
 */
function lerNextCursor(payload: object): string | null | undefined {
  if (!('nextCursor' in payload)) return undefined;
  const bruto = (payload as { nextCursor?: unknown }).nextCursor;
  return typeof bruto === 'string' && bruto.length > 0 ? bruto : null;
}

function parseListResponse(body: RawProcessosListResponse): ListProcessosResponse {
  const payload = body?.data ?? body;
  // Corpo que não é um OBJETO de página não pode ser absorvido: sem a chave `nextCursor`,
  // o painel leria a resposta malformada como core sem suporte a cursor (REQ-07) — toast
  // de "ambiente não suporta" e `addError('triagem_cursor_sem_suporte')` apontando deploy
  // fora de ordem onde houve outra coisa. Aquele sinal existe para diagnosticar UMA coisa.
  // Falha de carga é o diagnóstico honesto, e é o que o código fazia antes deste PR (o
  // `payload.items` estourava `TypeError`) — aqui só ganha mensagem.
  //
  // `Array.isArray` junto porque `typeof [] === 'object'`: um corpo com raiz `[]` (ou
  // `{ data: [] }`) atravessaria o guard e viraria `items: []` com `nextCursor` ausente,
  // que é exatamente a forma que este guard existe para não deixar passar.
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('A listagem de processos não veio no formato de página.');
  }
  const anteriores = (payload as { anteriores?: unknown }).anteriores;
  return {
    items: payload.items ?? [],
    total: payload.total ?? 0,
    page: payload.page ?? 1,
    limit: payload.limit ?? 10,
    nextCursor: lerNextCursor(payload),
    // Contador do servidor: só inteiro seguro e **não negativo** vira posição. `NaN`,
    // fracionário, não-número e negativo caem no degradado ("M casos"), que é melhor que
    // um "Caso NaN de 2100" ou um "Caso −4". `>= 0` e não `> 0`: `0` é o valor legítimo
    // da primeira página.
    anteriores:
      typeof anteriores === 'number' && Number.isSafeInteger(anteriores) && anteriores >= 0
        ? anteriores
        : undefined,
  };
}

/**
 * O dia da sentença vem uma vez na lista (`intimacaoSentencaDesde`), mas quem precisa dele é a ação
 * de cada requerido: copiá-lo para a intimação de cada parte evita um seletor novo só para levar
 * o `min` da data até o drawer.
 */
function comDiaDaSentencaNasPartes(
  partes: ParteCompletaItem[],
  desde: unknown,
): ParteCompletaItem[] {
  const dia = typeof desde === 'string' ? desde : null;
  return partes.map((parte) =>
    parte.intimacaoSentenca
      ? { ...parte, intimacaoSentenca: { ...parte.intimacaoSentenca, desde: dia } }
      : parte,
  );
}

/**
 * Rota irmã do `/download`, no mesmo par que o core registra (etapa 3a da D12): devolve a
 * MESMA signed URL, só que em JSON. O redirect da `/download` não serve a quem busca por XHR
 * — `request()` manda `credentials: 'include'`, e fetch credenciada não segue redirect
 * cross-origin para o GCS.
 */
function paraEndpointUrlAssinada(endpointDownload: string): string {
  return `${endpointDownload}-url`;
}

/**
 * Credencial de leitura de vida curta (`GCS_SIGNED_URL_TTL`, 300s): peça imediatamente antes
 * de usar, nunca guarde em estado de lista.
 */
async function obterUrlAssinadaDoDocumento(
  endpoint: string,
  signal?: AbortSignal,
): Promise<string> {
  const response = await request({
    endpoint: paraEndpointUrlAssinada(endpoint),
    method: 'GET',
    signal,
  });
  if (!response.ok) await handleError(response, 'Erro ao gerar o link do documento');
  const payload = unwrapData<{ url?: unknown }>(await response.json());
  return assertUrlNavegavelSegura(payload?.url, 'Link do documento');
}

/**
 * Dois saltos de propósito: o core assina, o browser busca os bytes DIRETO do bucket.
 *
 * O caminho antigo (`?inline=1`) fazia o core bufferizar o arquivo inteiro e devolvê-lo na
 * resposta — o que estoura o teto de 32 MiB de resposta não-streamada do Cloud Run e derrubava
 * com 500 todo documento grande (decisão D12, etapa 3b).
 */
async function buscarConteudoDoBucket(endpoint: string, signal?: AbortSignal): Promise<Blob> {
  return buscarBytesRemotos(await obterUrlAssinadaDoDocumento(endpoint, signal), signal);
}

export const secretariaArbitralService = {
  obterConteudoDocumento: (endpoint: string, signal?: AbortSignal): Promise<Blob> =>
    buscarConteudoDoBucket(endpoint, signal),

  obterUrlDocumento: (endpoint: string, signal?: AbortSignal): Promise<string> =>
    obterUrlAssinadaDoDocumento(endpoint, signal),

  baixarDocumento: async (
    endpoint: string,
    nomeArquivo: string,
    signal?: AbortSignal,
  ): Promise<void> => {
    salvarBlobComoArquivo(await buscarConteudoDoBucket(endpoint, signal), nomeArquivo);
  },

  listarProcessos: async (filters: ListProcessosFilters): Promise<ListProcessosResponse> => {
    const params = buildListParams(filters);
    const response = await apiGet('/admin/arbitral/processos', params);
    if (!response.ok) await handleError(response, 'Erro ao listar processos arbitrais');
    const body = (await response.json()) as RawProcessosListResponse;
    return parseListResponse(body);
  },

  validarLote: async (input: LoteValidacaoInput): Promise<LoteValidacaoResponse> => {
    const response = await apiPost('/admin/arbitral/processos/lote/validar', input);
    if (!response.ok) await handleError(response, 'Erro ao validar o lote');
    return unwrapData<LoteValidacaoResponse>(await response.json());
  },

  executarLote: async (input: LoteRequestInput): Promise<LoteExecucaoResponse> => {
    const response = await apiPost('/admin/arbitral/processos/lote', input);
    if (response.status === 409) {
      await lancarConflitoIdempotencia(response, {
        chave: 'loteId',
        mensagemPadrao: 'Execução em lote já solicitada',
      });
    }
    if (!response.ok) await handleError(response, 'Erro ao executar o lote');
    return unwrapData<LoteExecucaoResponse>(await response.json());
  },

  obterProcessosFacets: async (filters: ListProcessosFilters): Promise<ProcessosFacets> => {
    const { page: _page, limit: _limit, sortBy: _sortBy, sortOrder: _sortOrder, ...rest } = filters;
    const response = await apiGet('/admin/arbitral/processos/facets', buildListParams(rest));
    if (!response.ok) await handleError(response, 'Erro ao carregar contagens de processos');
    return unwrapData<ProcessosFacets>(await response.json());
  },

  obterPainelGeralCounts: async (): Promise<PainelGeralCounts> => {
    const response = await apiGet('/admin/arbitral/painel/counts');
    if (!response.ok) await handleError(response, 'Erro ao carregar painel');
    const body = await response.json();
    return (body?.data ?? body) as PainelGeralCounts;
  },

  obterResumoTriagem: async (): Promise<ResumoTriagem> => {
    const response = await apiGet('/admin/arbitral/processos/triagem/resumo');
    if (!response.ok) await handleError(response, 'Erro ao carregar contadores da triagem');
    const body = await response.json();
    return (body?.data ?? body) as ResumoTriagem;
  },

  iniciarTriagem: async (processoId: string): Promise<{ processoArbitralId: string }> => {
    const response = await apiPost(`/admin/arbitral/processos/${processoId}/triagem/iniciar`);
    if (!response.ok) await handleError(response, 'Erro ao iniciar triagem');
    const body = await response.json();
    return body?.data ?? body;
  },

  decidirTriagem: async (
    processoId: string,
    decisao: TriagemDecisao,
  ): Promise<TriagemDecisaoAgendada> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/triagem`,
      toTriagemDecisaoApiBody(decisao),
    );
    if (!response.ok) await handleError(response, 'Erro ao decidir triagem');
    const body = await response.json();
    return body?.data ?? body;
  },

  obterDecisaoTriagemAtiva: async (processoId: string): Promise<TriagemDecisaoAtiva | null> => {
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/triagem/decisao`);
    if (!response.ok) await handleError(response, 'Erro ao consultar decisão de triagem');
    const body = await response.json();
    return (body?.data ?? null) as TriagemDecisaoAtiva | null;
  },

  desfazerTriagem: async (processoId: string): Promise<{ decisaoId: string }> => {
    const response = await apiPost(`/admin/arbitral/processos/${processoId}/triagem/desfazer`);
    if (!response.ok) await handleError(response, 'Erro ao desfazer decisão de triagem');
    const body = await response.json();
    return body?.data ?? body;
  },

  obterChecklistTriagem: async (processoId: string): Promise<ChecklistTriagem> => {
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/triagem/checklist`);
    if (!response.ok) await handleError(response, 'Erro ao carregar checklist de triagem');
    const body = await response.json();
    return body?.data ?? body;
  },

  salvarChecklistTriagem: async (
    processoId: string,
    itens: ChecklistTriagemAvaliacaoInput[],
    origemAvaliacao?: OrigemAvaliacaoChecklist,
  ): Promise<{ quantidadeAvaliada: number }> => {
    const response = await apiPut(`/admin/arbitral/processos/${processoId}/triagem/checklist`, {
      itens,
      ...(origemAvaliacao ? { origemAvaliacao } : {}),
    });
    if (!response.ok) await handleError(response, 'Erro ao salvar avaliações do checklist');
    const body = await response.json();
    return body?.data ?? body;
  },

  listarAuditoriaTriagem: async (
    processoId: string,
    cursor?: string,
  ): Promise<AuditoriaTriagemPage> => {
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/triagem/auditoria`, {
      limit: LIMITE_PAGINA_AUDITORIA_TRIAGEM,
      ...(cursor ? { cursor } : {}),
    });
    if (!response.ok) await handleError(response, 'Erro ao carregar a auditoria da triagem');
    const body = await response.json();
    const payload = body?.data ?? body;
    return {
      items: (payload?.items ?? []) as AuditoriaTriagemItem[],
      nextCursor: (payload?.nextCursor ?? null) as string | null,
      hasMore: Boolean(payload?.hasMore),
    };
  },

  registrarExportacaoTriagem: async (
    processoIds: string[],
    formato: 'csv' | 'xlsx',
  ): Promise<void> => {
    const response = await apiPost('/admin/arbitral/processos/triagem/exportacao-registrada', {
      processoIds,
      formato,
    });
    if (!response.ok) await handleError(response, 'Erro ao registrar exportação na auditoria');
  },

  iniciarTriagemLote: async (processoIds: string[]): Promise<ResultadoLoteTriagem> => {
    const response = await apiPost('/admin/arbitral/processos/triagem/iniciar-lote', {
      processoIds,
    });
    if (!response.ok) await handleError(response, 'Erro ao iniciar triagem em lote');
    const body = await response.json();
    return body?.data ?? body;
  },

  decidirTriagemLote: async (
    processoIds: string[],
    decisao: TriagemDecisaoLote,
  ): Promise<ResultadoLoteTriagem> => {
    const response = await apiPost('/admin/arbitral/processos/triagem/decidir-lote', {
      processoIds,
      decisao: toTriagemDecisaoLoteApiBody(decisao, processoIds),
    });
    if (!response.ok) await handleError(response, 'Erro ao decidir triagem em lote');
    const body = await response.json();
    return body?.data ?? body;
  },

  registrarConcluso: async (processoId: string, input: ConclusoInput): Promise<ConclusoResult> => {
    const response = await apiPost(`/admin/arbitral/processos/${processoId}/concluso`, input);
    if (!response.ok) await handleError(response, 'Erro ao registrar concluso');
    const body = await response.json();
    return body?.data ?? body;
  },

  sugerirSorteio: async (processoId: string, body: SorteioBody): Promise<SorteioSugestao> => {
    const response = await apiPost(`/admin/arbitral/processos/${processoId}/sorteio/sugerir`, body);
    if (!response.ok) await handleError(response, 'Erro ao sugerir sorteio');
    const result = await response.json();
    return result?.data ?? result;
  },

  confirmarSorteio: async (processoId: string, body: SorteioBody): Promise<SorteioResult> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/sorteio/confirmar`,
      body,
    );
    if (!response.ok) await handleError(response, 'Erro ao confirmar sorteio');
    const result = await response.json();
    return result?.data ?? result;
  },

  registrarManifestacao: async (
    processoId: string,
    manifestacaoId: string,
    descricaoAndamento?: string,
  ): Promise<{ id: string }> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/manifestacoes/${manifestacaoId}/registrar`,
      { descricaoAndamento },
    );
    if (!response.ok) await handleError(response, 'Erro ao registrar manifestação');
    const result = await response.json();
    return result?.data ?? result;
  },

  desconsiderarManifestacao: async (
    processoId: string,
    manifestacaoId: string,
    justificativa: string,
  ): Promise<{ id: string }> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/manifestacoes/${manifestacaoId}/desconsiderar`,
      { justificativa },
    );
    if (!response.ok) await handleError(response, 'Erro ao desconsiderar manifestação');
    const result = await response.json();
    return result?.data ?? result;
  },

  obterElegibilidadeTransito: async (processoId: string): Promise<TransitoElegibilidade> => {
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/transito/elegibilidade`);
    if (!response.ok) await handleError(response, 'Erro ao consultar elegibilidade do trânsito');
    const result = await response.json();
    return result?.data ?? result;
  },

  publicarTransito: async (
    processoId: string,
    body: PublicarTransitoBody,
  ): Promise<PublicarTransitoResult> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/transito/publicar`,
      body,
    );
    if (!response.ok) await handleError(response, 'Erro ao publicar trânsito');
    const result = await response.json();
    return result?.data ?? result;
  },

  listarProcessosPorStatus: async (
    status: ProcessoArbitralStatus[],
    limit = 50,
  ): Promise<ProcessoArbitralListItem[]> => {
    const list = await secretariaArbitralService.listarProcessos({ status, limit });
    return list.items;
  },

  listarPrazosAbertos: async (query: PrazosPainelQuery = {}): Promise<PrazosPainelData> => {
    const params: Record<string, string> = { somenteAtivos: 'true' };
    if (query.from) params.from = query.from;
    if (query.to) params.to = query.to;
    if (query.q?.trim()) params.q = query.q.trim();
    if (query.responsavel?.length) params.responsavel = query.responsavel.join(',');
    if (query.tipo?.length) params.tipo = query.tipo.join(',');
    const response = await apiGet('/admin/arbitral/prazos', params);
    if (!response.ok) await handleError(response, 'Erro ao listar prazos');
    const body = await response.json();
    const payload = body?.data ?? body;
    const items = ((payload?.items ?? []) as PrazoPainelApiItem[]).map(mapearPrazoApi);
    return {
      items,
      resumo: (payload?.resumo ?? {
        total: items.length,
        vencidos: 0,
        venceHoje: 0,
      }) as PrazosPainelResumo,
      contadores: (payload?.contadores ?? {
        responsavel: { SECRETARIA: 0, ARBITRO: 0, PARTE: 0, SISTEMA: 0 },
        tipo: {},
      }) as PrazosPainelContadores,
    };
  },

  reabrirProcesso: async (
    processoId: string,
    body: ReabrirProcessoBody,
  ): Promise<ReabrirProcessoResult> => {
    const response = await apiPost(`/admin/arbitral/processos/${processoId}/reabrir`, body);
    if (!response.ok) await handleError(response, 'Erro ao reabrir processo');
    return unwrapData<ReabrirProcessoResult>(await response.json());
  },

  liberarSentenca: async (
    processoId: string,
    body: LiberarSentencaBody,
  ): Promise<LiberarSentencaResult> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/sentenca/liberar`,
      body,
    );
    if (!response.ok) await handleError(response, 'Erro ao liberar a sentença');
    return unwrapData<LiberarSentencaResult>(await response.json());
  },

  obterDetalheProcesso: async (processoId: string): Promise<ProcessoDetalhe> => {
    const response = await apiGet(
      `/admin/arbitral/processos/${processoId}/detalhe`,
      undefined,
      false,
      AbortSignal.timeout(TIMEOUT_LEITURA_MS),
    );
    if (!response.ok) await handleError(response, 'Erro ao carregar detalhe do processo');
    const body = await response.json();
    return normalizarDetalheProcesso(body?.data ?? body);
  },

  listarManifestacoes: async (
    processoId: string,
    options: { somenteRegistradas?: boolean; limit?: number } = {},
  ): Promise<ManifestacaoListItem[]> => {
    const params: Record<string, string | number | boolean> = {};
    if (options.somenteRegistradas) params.somenteRegistradas = 'true';
    if (options.limit) params.limit = options.limit;
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/manifestacoes`, params);
    if (!response.ok) await handleError(response, 'Erro ao listar manifestações');
    const body = await response.json();
    const payload = body?.data ?? body;
    return (payload?.items ?? []) as ManifestacaoListItem[];
  },

  criarManifestacao: async (
    processoId: string,
    input: CriarManifestacaoInput,
  ): Promise<ManifestacaoListItem> => {
    const dataRecebimento = dataRecebimentoParaEnviar(input.diaRecebimento, hojeEmSaoPaulo());
    const anexoUploadIds: string[] = [];
    for (const arquivo of input.arquivos) {
      const enviado = await secretariaArbitralService.enviarAnexoDoProcesso(processoId, arquivo, {
        sigilo: input.sigilo,
      });
      anexoUploadIds.push(enviado.uploadId);
    }
    const conteudo = input.conteudo?.trim();
    const response = await apiPost(`/admin/arbitral/processos/${processoId}/manifestacoes`, {
      parteArbitralId: input.parteArbitralId,
      canal: input.canal,
      submissionId: input.submissionId,
      ...(dataRecebimento ? { dataRecebimento } : {}),
      ...(conteudo ? { conteudo } : {}),
      ...(anexoUploadIds.length > 0 ? { anexoUploadIds } : {}),
    });
    if (!response.ok) await handleError(response, 'Erro ao registrar a manifestação');
    return unwrapData<ManifestacaoListItem>(await response.json());
  },

  validarPendencia: async (
    processoId: string,
    pendenciaId: string,
    input: { acao: ValidacaoPendenciaAcao; motivo?: string } & Partial<
      { prazoDiasUteis: number } & { dataVencimento: string }
    >,
  ): Promise<ValidarPendenciaResult> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/pendencias/${pendenciaId}/validar`,
      input,
    );
    if (!response.ok) await handleError(response, 'Erro ao validar pendência');
    const body = await response.json();
    return (body?.data ?? body) as ValidarPendenciaResult;
  },

  listarPendenciasDoProcesso: async (
    processoId: string,
    options: ListarPendenciasFilters = {},
  ): Promise<ListarPendenciasSecretariaResult> => {
    const params: Record<string, string | number | boolean> = {};
    if (options.filtro) params.filtro = options.filtro;
    if (options.page) params.page = options.page;
    if (options.limit) params.limit = options.limit;
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/pendencias`, params);
    if (!response.ok) await handleError(response, 'Erro ao listar pendências');
    return parseListaPendencias(await response.json());
  },

  criarPendenciaNoProcesso: async (
    processoId: string,
    payload: CriarPendenciaPayload,
  ): Promise<CriarPendenciaResultado> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/pendencias`,
      toCriarPendenciaBody(payload),
    );
    if (!response.ok) await handleError(response, 'Erro ao criar a pendência');
    return unwrapData<CriarPendenciaResultado>(await response.json());
  },

  movimentarPendencias: async (
    payload: MovimentarPendenciasPayload,
  ): Promise<MovimentarPendenciasResultado> => {
    const response = await apiPost(
      '/admin/arbitral/pendencias/movimentacoes',
      toMovimentarPendenciasBody(payload),
    );
    if (!response.ok) await handleError(response, 'Erro ao movimentar as pendências');
    return unwrapData<MovimentarPendenciasResultado>(await response.json());
  },

  /**
   * Data que o chip de dias úteis vai gravar, calculada pelo calendário da câmara. O front
   * só sabe pular fim de semana; feriado só o backend conhece — daí a prévia vir de lá em vez
   * de a tela estimar e o 201 devolver outra data.
   */
  previewPrazoPendencia: async (diasUteis: number): Promise<PrazoPendenciaPreview> => {
    const simulacao = await feriadosService.simularPrazo({
      dataInicial: hojeEmSaoPaulo(),
      diasUteis,
    });
    return {
      diasUteis,
      dataVencimento: simulacao.dataFinal,
      anoSemCalendario: simulacao.anoSemCalendario,
    };
  },

  publicarDecurso: async (
    processoId: string,
    body: PublicarDecursoBody = {},
  ): Promise<PublicarDecursoResult> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/decurso/publicar`,
      body,
    );
    if (!response.ok) await handleError(response, 'Erro ao publicar decurso');
    const result = await response.json();
    return (result?.data ?? result) as PublicarDecursoResult;
  },

  listarRankingArbitros: async (): Promise<RankingArbitroItem[]> => {
    const response = await apiGet('/admin/arbitral/ranking-arbitros');
    if (!response.ok) await handleError(response, 'Erro ao listar ranking de árbitros');
    const body = await response.json();
    const payload = body?.data ?? body;
    return (payload?.items ?? []) as RankingArbitroItem[];
  },

  substituirRankingArbitros: async (itens: RankingArbitroItem[]): Promise<RankingArbitroItem[]> => {
    const response = await apiPut('/admin/arbitral/ranking-arbitros', { itens });
    if (!response.ok) await handleError(response, 'Erro ao atualizar ranking de árbitros');
    const body = await response.json();
    const payload = body?.data ?? body;
    return (payload?.items ?? []) as RankingArbitroItem[];
  },

  listarAr: async (options: ArListFiltros = {}): Promise<ArListResult> => {
    const params: Record<string, string | number | boolean> = {};
    if (options.page) params.page = options.page;
    if (options.limit) params.limit = options.limit;
    if (options.search) params.search = options.search;
    if (options.origem) params.origem = options.origem;
    if (options.prazo) params.prazo = options.prazo;
    if (options.status && options.status.length > 0) params.status = options.status.join(',');
    const response = await apiGet('/admin/arbitral/ar', params);
    if (!response.ok) await handleError(response, 'Erro ao listar AR');
    const body = await response.json();
    const payload = body?.data ?? body;
    return {
      items: (payload?.items ?? []) as ArListItem[],
      total: payload?.total ?? 0,
      page: payload?.page ?? options.page ?? 1,
      limit: payload?.limit ?? options.limit ?? 20,
      resumo: payload?.resumo ?? {
        total: 0,
        emCurso: 0,
        vencidos: 0,
        retornados: 0,
        aguardandoAutorizacao: 0,
      },
      porOrigem: payload?.porOrigem ?? { arbitral: 0, extrajudicial: 0 },
    };
  },

  listarArAEnviar: async (filtros: ArAEnviarFiltros = {}): Promise<ArAEnviarResult> => {
    const params: Record<string, string | number> = {};
    if (filtros.page) params.page = filtros.page;
    if (filtros.limit) params.limit = filtros.limit;
    if (filtros.search) params.search = filtros.search;
    const response = await apiGet('/admin/arbitral/ar/a-enviar', params);
    if (!response.ok) await handleError(response, 'Erro ao carregar a fila de envios');
    const payload = unwrapData<ArAEnviarResult>(await response.json());
    return {
      items: payload?.items ?? [],
      total: payload?.total ?? 0,
      totalGeral: payload?.totalGeral ?? payload?.total ?? 0,
      page: payload?.page ?? filtros.page ?? 1,
      limit: payload?.limit ?? filtros.limit ?? 20,
    };
  },

  obterRequeridoAEnviar: async (parteArbitralId: string): Promise<RequeridoAEnviarDetalhe> => {
    const response = await apiGet(`/admin/arbitral/ar/a-enviar/${parteArbitralId}`);
    // 404/409 sobem como ErroHttp com `codigo` — o hook os traduz em "saiu da fila".
    if (!response.ok) await handleError(response, 'Erro ao carregar o requerido');
    return unwrapData<RequeridoAEnviarDetalhe>(await response.json());
  },

  /**
   * Consulta de CEP. **Não lança nos dois desfechos esperados** — devolve resultado
   * discriminado, porque "CEP não existe" e "não deu para conferir agora" são
   * informação para a tela, não exceção: a segunda não pode acusar o endereço.
   */
  consultarCep: async (cep: string): Promise<ConsultaCepResultado> => {
    const response = await apiGet(`/admin/arbitral/cep/${cep.replace(/\D/g, '')}`);
    if (response.status === 404) return { ok: false, motivo: 'NAO_ENCONTRADO' };
    if (!response.ok) return { ok: false, motivo: 'INDISPONIVEL' };
    const endereco = unwrapData<{
      logradouro: string;
      bairro: string;
      cidade: string;
      uf: string;
    }>(await response.json());
    return { ok: true, endereco };
  },

  cotarEnvioAr: async (cep: string): Promise<CotacaoEnvioAr> => {
    const response = await apiPost('/admin/arbitral/ar/cotacoes', {
      cep: cep.replace(/\D/g, ''),
    });
    if (!response.ok) await handleError(response, 'Erro ao calcular o valor do envio');
    return unwrapData<CotacaoEnvioAr>(await response.json());
  },

  solicitarEnvioAr: async (
    payload: SolicitarEnvioArPayload,
  ): Promise<SolicitarEnvioArResultado> => {
    const response = await apiPost('/admin/arbitral/ar/solicitacoes', payload);
    if (response.status === 409) {
      const body = await response.json().catch(() => null);
      const codigo = body?.error?.code ?? body?.code ?? null;
      if (codigo === CODIGOS_SOLICITACAO_AR.COTACAO_DEFASADA) {
        // A cotação nova vem no 409 para a tela repovoar sem uma segunda ida.
        throw new CotacaoDefasadaError(
          body?.error?.message ?? 'O valor do envio mudou. Recalcule antes de solicitar.',
          body?.error?.details?.cotacao ?? null,
        );
      }
      throw new ErroHttp(
        body?.error?.message ?? body?.message ?? 'Não foi possível solicitar o envio',
        409,
        null,
        codigo,
      );
    }
    if (!response.ok) await handleError(response, 'Erro ao solicitar o envio de AR');
    return unwrapData<SolicitarEnvioArResultado>(await response.json());
  },

  /**
   * Reemite a cobrança da solicitação cuja emissão falhou. **Sem corpo**: o alvo é a
   * solicitação e o valor é o do snapshot dela — deixar a tela mandar valor aqui
   * cobraria do requerente um número que ele nunca viu na tela de autorização.
   *
   * A rota é limitada por tentativa (cada clique cancela uma fatura no gateway): o
   * 429 sobe como `ErroHttp` com o `Retry-After` que o `handleError` já lê.
   */
  reemitirCobrancaAr: async (solicitacaoId: string): Promise<ReemitirCobrancaArResultado> => {
    const response = await apiPost(
      `/admin/arbitral/ar/solicitacoes/${encodeURIComponent(solicitacaoId)}/cobranca/reemissao`,
    );
    if (!response.ok) await handleError(response, 'Não foi possível reemitir a cobrança');
    // Corpo ilegível não é falha da reemissão — ela já aconteceu. O desfecho fica
    // desconhecido e quem diz a verdade é a listagem recarregada.
    const corpo = unwrapData<{ desfecho?: string; situacaoCobranca?: SituacaoCobrancaAr } | null>(
      await response.json().catch(() => null),
    );
    return {
      solicitacaoId,
      desfecho: corpo?.desfecho ?? 'DESCONHECIDO',
      // `null` quando o servidor devolve só a AÇÃO: deduzir a situação a partir dela
      // mostraria "cobrança em aberto" para uma emissão que falhou de novo.
      situacaoCobranca: corpo?.situacaoCobranca ?? null,
    };
  },

  obterArDetalhe: async (envioFisicoId: string): Promise<ArDetalhe> => {
    const response = await apiGet(`/admin/arbitral/ar/${envioFisicoId}`);
    if (!response.ok) await handleError(response, 'Erro ao carregar detalhe do AR');
    const body = await response.json();
    return (body?.data ?? body) as ArDetalhe;
  },

  listarArDoProcesso: async (processoId: string): Promise<ArListItem[]> => {
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/envios-fisicos`);
    if (!response.ok) await handleError(response, 'Erro ao listar AR do processo');
    const body = await response.json();
    const payload = body?.data ?? body;
    return (payload?.items ?? []) as ArListItem[];
  },

  listarPartesCompletas: async (processoId: string): Promise<ParteCompletaItem[]> => {
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/partes`);
    if (!response.ok) await handleError(response, 'Erro ao listar partes do processo');
    const body = await response.json();
    const payload = body?.data ?? body;
    return comDiaDaSentencaNasPartes(
      (payload?.items ?? []) as ParteCompletaItem[],
      payload?.intimacaoSentencaDesde,
    );
  },

  consultarDocumentoDaParte: async (
    documento: string,
  ): Promise<ConsultaDocumentoParteResultado> => {
    const resultado = await documentoLookupService.validar(normalizeCpfCnpj(documento));
    const nome = resultado.nome?.trim() ?? '';
    return { nome: nome.length > 0 ? nome : null, documento: resultado.documento ?? null };
  },

  criarParte: async (
    processoId: string,
    input: CriarParteArbitralInput,
  ): Promise<CriarParteArbitralResultado> => {
    const response = await apiPost(`/admin/arbitral/processos/${processoId}/partes`, input);
    if (!response.ok) await handleError(response, 'Erro ao cadastrar parte do processo');
    return unwrapData<CriarParteArbitralResultado>(await response.json());
  },

  editarParte: async (
    processoId: string,
    parteArbitralId: string,
    input: EditarParteArbitralInput,
  ): Promise<EditarParteArbitralResultado> => {
    const response = await apiPatch(
      `/admin/arbitral/processos/${processoId}/partes/${parteArbitralId}`,
      input,
    );
    if (!response.ok) await handleError(response, 'Erro ao editar parte do processo');
    return unwrapData<EditarParteArbitralResultado>(await response.json());
  },

  listarAdvogadosDaParte: async (
    processoId: string,
    parteArbitralId: string,
  ): Promise<AdvogadoDaParteItem[]> => {
    const response = await apiGet(
      `/admin/arbitral/processos/${processoId}/partes/${parteArbitralId}/advogados`,
    );
    if (!response.ok) await handleError(response, 'Erro ao listar advogados da parte');
    const body = await response.json();
    const payload = body?.data ?? body;
    return (payload?.advogados ?? []) as AdvogadoDaParteItem[];
  },

  adicionarAdvogadoDaParte: async (
    processoId: string,
    parteArbitralId: string,
    input: AdicionarAdvogadoParteInput,
  ): Promise<AlterarAdvogadoResultado> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/partes/${parteArbitralId}/advogados`,
      input,
    );
    if (!response.ok) await handleError(response, 'Erro ao constituir advogado da parte');
    return unwrapData<AlterarAdvogadoResultado>(await response.json());
  },

  editarAdvogadoDaParte: async (
    processoId: string,
    parteArbitralId: string,
    advogadoParteArbitralId: string,
    input: EditarAdvogadoParteInput,
  ): Promise<AlterarAdvogadoResultado> => {
    const response = await apiPatch(
      `/admin/arbitral/processos/${processoId}/partes/${parteArbitralId}/advogados/${advogadoParteArbitralId}`,
      input,
    );
    if (!response.ok) await handleError(response, 'Erro ao editar advogado da parte');
    return unwrapData<AlterarAdvogadoResultado>(await response.json());
  },

  removerAdvogadoDaParte: async (
    processoId: string,
    parteArbitralId: string,
    advogadoParteArbitralId: string,
    motivo: string,
  ): Promise<RemoverAdvogadoResultado> => {
    const response = await request({
      endpoint: `/admin/arbitral/processos/${processoId}/partes/${parteArbitralId}/advogados/${advogadoParteArbitralId}`,
      method: 'DELETE',
      body: { motivo },
    });
    if (!response.ok) await handleError(response, 'Erro ao remover advogado da parte');
    return unwrapData<RemoverAdvogadoResultado>(await response.json());
  },

  desativarParte: async (
    processoId: string,
    parteArbitralId: string,
    motivo: string,
  ): Promise<AlterarSituacaoParteResultado> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/partes/${parteArbitralId}/desativar`,
      { motivo },
    );
    if (!response.ok) await handleError(response, 'Erro ao desativar parte do processo');
    return unwrapData<AlterarSituacaoParteResultado>(await response.json());
  },

  reativarParte: async (
    processoId: string,
    parteArbitralId: string,
    motivo: string,
  ): Promise<AlterarSituacaoParteResultado> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/partes/${parteArbitralId}/reativar`,
      { motivo },
    );
    if (!response.ok) await handleError(response, 'Erro ao reativar parte do processo');
    return unwrapData<AlterarSituacaoParteResultado>(await response.json());
  },

  removerParte: async (
    processoId: string,
    parteArbitralId: string,
    motivo: string,
  ): Promise<AlterarSituacaoParteResultado> => {
    const response = await request({
      endpoint: `/admin/arbitral/processos/${processoId}/partes/${parteArbitralId}`,
      method: 'DELETE',
      body: { motivo },
    });
    if (!response.ok) await handleError(response, 'Erro ao remover parte do processo');
    return unwrapData<AlterarSituacaoParteResultado>(await response.json());
  },

  reenviarLinkAcessoParte: async (
    processoId: string,
    parteArbitralId: string,
    input: ReenviarLinkAcessoInput,
  ): Promise<ReenviarLinkAcessoResultado> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/partes/${parteArbitralId}/acesso/reenviar-link`,
      input,
    );
    if (!response.ok) await handleError(response, 'Erro ao reenviar o link de acesso da parte');
    return unwrapData<ReenviarLinkAcessoResultado>(await response.json());
  },

  solicitarVerificacaoFacialParte: async (
    processoId: string,
    parteArbitralId: string,
    input: VerificacaoFacialParteInput,
  ): Promise<VerificacaoFacialParteResultado> => {
    const response = await apiPost(
      `/admin/arbitral/processos/${processoId}/partes/${parteArbitralId}/acesso/verificacao-facial`,
      input,
    );
    if (!response.ok) await handleError(response, 'Erro ao enviar a verificação facial');
    return unwrapData<VerificacaoFacialParteResultado>(await response.json());
  },

  listarDocumentosProcesso: async (processoId: string): Promise<DocumentoArbitralItem[]> => {
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/documentos`);
    if (!response.ok) await handleError(response, 'Erro ao listar documentos do processo');
    const body = await response.json();
    const payload = body?.data ?? body;
    return (payload?.items ?? []) as DocumentoArbitralItem[];
  },

  enviarAnexoDoProcesso: async (
    processoId: string,
    arquivo: File,
    opcoes: { sigilo?: boolean } = {},
  ): Promise<AnexoUploadEnviado> => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    if (opcoes.sigilo) form.append('sigilo', 'true');
    const response = await apiPost(`/admin/arbitral/processos/${processoId}/documentos`, form);
    if (!response.ok) await handleError(response, 'Erro ao enviar o anexo do processo');
    const uploadId = extrairUploadIdCriado(unwrapData<unknown>(await response.json()));
    if (!uploadId) throw new Error('O envio do anexo não devolveu o uploadId');
    return { uploadId, nomeOriginal: arquivo.name, tamanhoBytes: arquivo.size };
  },

  publicarAndamentoProcesso: async (
    input: PublicarAndamentoInput,
  ): Promise<PublicarAndamentoResultado> => {
    const form = new FormData();
    form.append('conteudo', input.conteudo);
    aplicarCamposAndamento(form, input);
    if (input.criarPendencia) {
      form.append('criarPendencia', 'true');
      if (input.pendenciaDestinatario) {
        form.append('pendenciaDestinatario', input.pendenciaDestinatario);
      }
      if (input.prazo?.tipo === 'diasUteis') {
        form.append('prazoDiasUteis', String(input.prazo.dias));
      }
      if (input.prazo?.tipo === 'data') form.append('prazoData', input.prazo.data);
    }
    anexarArquivosAndamento(form, input.arquivos);

    const response = await apiPost(
      `/admin/arbitral/processos/${input.processoId}/andamentos`,
      form,
      false,
      AbortSignal.timeout(timeoutDaPublicacaoMs(input.arquivos)),
    );
    if (response.status === 409) {
      await lancarConflitoIdempotencia(response, {
        chave: 'andamentoId',
        mensagemPadrao: 'Este andamento já foi publicado',
      });
    }
    if (!response.ok) await handleError(response, 'Erro ao publicar o andamento');
    return unwrapData<PublicarAndamentoResultado>(await response.json());
  },

  listarDiasNaoUteis: async (ano: number): Promise<DiasNaoUteisDoAno> => {
    const response = await apiGet(
      `/admin/holidays/calendars/${ano}/dias-nao-uteis`,
      undefined,
      false,
      AbortSignal.timeout(TIMEOUT_LEITURA_MS),
    );
    if (!response.ok)
      await handleError(response, 'Erro ao carregar o calendário de dias não úteis');
    return unwrapData<DiasNaoUteisDoAno>(await response.json());
  },

  listarModelosAndamentoOpcoes: async (): Promise<ModeloAndamentoOpcao[]> => {
    const response = await apiGet(
      '/admin/arbitral/modelos-andamento/opcoes',
      undefined,
      false,
      AbortSignal.timeout(TIMEOUT_LEITURA_MS),
    );
    if (!response.ok) await handleError(response, 'Erro ao listar modelos de andamento');
    return unwrapData<ModeloAndamentoOpcao[]>(await response.json()) ?? [];
  },

  obterTextoModeloAndamento: async (
    processoId: string,
    modeloId: string,
    prazoDiasUteis?: number,
  ): Promise<TextoModeloMaterializado> => {
    const query = prazoDiasUteis === undefined ? '' : `?prazoDiasUteis=${prazoDiasUteis}`;
    const response = await apiGet(
      `/admin/arbitral/processos/${processoId}/modelos-andamento/${modeloId}/texto${query}`,
      undefined,
      false,
      AbortSignal.timeout(TIMEOUT_LEITURA_MS),
    );
    if (!response.ok) await handleError(response, 'Erro ao carregar o texto do modelo');
    return unwrapData<TextoModeloMaterializado>(await response.json());
  },

  listarAndamentosProcesso: async (processoId: string): Promise<AndamentoArbitralItem[]> => {
    const response = await apiGet(
      `/admin/arbitral/processos/${processoId}/andamentos`,
      undefined,
      false,
      AbortSignal.timeout(TIMEOUT_LEITURA_MS),
    );
    if (!response.ok) await handleError(response, 'Erro ao listar andamentos do processo');
    const body = await response.json();
    const payload = body?.data ?? body;
    return parseAndamentosProcesso(payload?.items);
  },

  listarSolicitacoesProcesso: async (
    processoId: string,
    filtro: FiltroSolicitacoes,
  ): Promise<SolicitacoesSecretariaResposta> => {
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/solicitacoes`, {
      filtro,
    });
    if (!response.ok) await handleError(response, 'Erro ao listar solicitações do processo');
    return normalizarSolicitacoesResposta(unwrapData<unknown>(await response.json()));
  },

  decidirSolicitacao: async (
    input: DecidirSolicitacaoInput,
  ): Promise<DecidirSolicitacaoResultado> => {
    const form = new FormData();
    form.append('modo', input.modo);
    form.append('justificativa', input.justificativa);
    aplicarCamposAndamento(form, input);
    anexarArquivosAndamento(form, input.anexos);

    const response = await apiPost(
      `/admin/arbitral/processos/${input.processoId}/solicitacoes/${input.solicitacaoId}/decidir`,
      form,
    );
    // TODO(#1165): o 409 aqui é de ESTADO (já respondida), não claim de clientRequestId —
    // o core só loga esse campo. Reconciliar a lista é o que o backend pede na mensagem.
    if (response.status === 409) {
      await lancarConflitoIdempotencia(response, {
        chave: 'solicitacaoId',
        mensagemPadrao: 'Solicitação já foi respondida — recarregue a aba',
      });
    }
    if (!response.ok) await handleError(response, 'Erro ao registrar a decisão da solicitação');
    return unwrapData<DecidirSolicitacaoResultado>(await response.json());
  },

  concluirSolicitacao: async (
    input: ConcluirSolicitacaoInput,
  ): Promise<ConcluirSolicitacaoResultado> => {
    const form = new FormData();
    form.append('mensagem', input.mensagem);
    aplicarCamposAndamento(form, input);
    anexarArquivosAndamento(form, input.anexos);

    const response = await apiPost(
      `/admin/arbitral/processos/${input.processoId}/solicitacoes/${input.solicitacaoId}/concluso`,
      form,
    );
    // TODO(#1165): idem — 409 de estado (já respondida ou já conclusa), não de idempotência.
    if (response.status === 409) {
      await lancarConflitoIdempotencia(response, {
        chave: 'solicitacaoId',
        mensagemPadrao: 'Solicitação já foi respondida ou conclusa — recarregue a aba',
      });
    }
    if (!response.ok) await handleError(response, 'Erro ao concluir a solicitação ao árbitro');
    return unwrapData<ConcluirSolicitacaoResultado>(await response.json());
  },

  listarCertificadosProcesso: async (processoId: string): Promise<CertificadoArbitralItem[]> => {
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/certificados`);
    if (!response.ok) await handleError(response, 'Erro ao listar certificados do processo');
    const body = await response.json();
    const payload = body?.data ?? body;
    return (payload?.items ?? []) as CertificadoArbitralItem[];
  },

  obterCertificadoAcessosProcesso: async (
    processoId: string,
  ): Promise<CertificadoAcessosArbitral> => {
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/certificado-acessos`);
    if (!response.ok) await handleError(response, 'Erro ao carregar o Certificado de Acessos');
    const body = await response.json();
    const dto = certificadoAcessosDtoSchema.parse(body?.data ?? body);
    return mapCertificadoAcessos(dto);
  },

  obterUrlCertificadoDownload: async (
    processoId: string,
    certificadoId: string,
  ): Promise<string> => {
    const response = await apiGet(
      `/admin/arbitral/processos/${processoId}/certificados/${certificadoId}/download`,
    );
    if (!response.ok) await handleError(response, 'Erro ao gerar o link da certidão');
    const body = await response.json();
    const payload = body?.data ?? body;
    return assertUrlNavegavelSegura(payload?.url, 'Link da certidão');
  },

  listarAlertasAtivos: async ({
    processoIds,
  }: ListarAlertasAtivosParams): Promise<ArbitralAlertasAtivosPagina> => {
    // Uma página de 100 (o teto do core) cobre os alertas dos até 20 processos da tela.
    const response = await apiGet('/admin/arbitral/alertas', {
      processoIds: processoIds.join(','),
      page: 1,
      limit: LIMITE_DE_ALERTAS_DA_TELA,
    });
    if (!response.ok) await handleError(response, 'Erro ao listar alertas ativos');
    const body = await response.json();
    return paginaDeAlertasAtivos(body?.data ?? body);
  },

  dispensarAlerta: async (
    alertaId: string,
    motivo: ArbitralAlertaDispensaMotivo,
  ): Promise<ArbitralAlertaItem> => {
    const response = await apiPost(`/admin/arbitral/alertas/${alertaId}/dispensar`, { motivo });
    if (!response.ok) await handleError(response, 'Erro ao dispensar alerta');
    const body = await response.json();
    return (body?.data ?? body) as ArbitralAlertaItem;
  },

  listarChatProcesso: async (
    processoId: string,
    options: { page?: number; limit?: number } = {},
  ): Promise<ChatListPage> => {
    const params: Record<string, string | number | boolean> = {};
    if (options.page) params.page = options.page;
    if (options.limit) params.limit = options.limit;
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/chat`, params);
    if (!response.ok) await handleError(response, 'Erro ao listar chat do processo');
    const body = await response.json();
    const payload = body?.data ?? body;
    return {
      mensagens: payload?.mensagens ?? [],
      total: payload?.total ?? 0,
      page: payload?.page ?? 1,
      limit: payload?.limit ?? 50,
    } as ChatListPage;
  },
};
